import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { salesApi, apiError } from '../salesApi';
import { useT } from '../salesI18n';
import { useSalesAuth } from '../SalesAuthContext';

const TYPES = ['measurement', 'sales', 'follow_up'];
const todayStr = () => new Date().toISOString().slice(0, 10);

// lead: lead fixo (vindo do detalhe). leads: lista p/ escolher (vindo da agenda). visit: editar.
export const VisitFormDialog = ({ open, onOpenChange, lead, leads = [], visit, defaultDate, onSaved }) => {
  const { t } = useT();
  const { user } = useSalesAuth();
  const [form, setForm] = useState({ lead_id: '', date: todayStr(), time: '09:00', duration_min: 60, type: 'measurement', notes: '', assigned_to: '' });
  const [team, setTeam] = useState([]);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(visit?.id);

  useEffect(() => {
    if (!open) return;
    setForm({
      lead_id: visit?.lead_id || lead?.id || '',
      date: visit?.date || defaultDate || todayStr(),
      time: visit?.time || '09:00',
      duration_min: visit?.duration_min || 60,
      type: visit?.type || 'measurement',
      notes: visit?.notes || '',
      assigned_to: visit?.assigned_to || user?.username || '',
    });
    if (user?.role === 'manager') salesApi.get('/team').then((r) => setTeam(r.data)).catch(() => {});
  }, [open, visit, lead, defaultDate, user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.lead_id) { toast.error(t.selectLead); return; }
    setSaving(true);
    try {
      const payload = { ...form, duration_min: Number(form.duration_min) || 60, notes: form.notes || null, assigned_to: form.assigned_to || null };
      const { data } = isEdit ? await salesApi.put(`/visits/${visit.id}`, payload) : await salesApi.post('/visits', payload);
      toast.success(t.visitSaved);
      if (data.customer_email?.ok) toast.success(t.visitEmailSent(data.customer_email.to));
      else if (data.customer_email?.error === 'no_email') toast.warning(t.visitEmailNo);
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="visit-form-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? t.editVisit : t.newVisit}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {lead ? (
            <div className="rounded-lg border bg-muted/40 px-3 py-2 text-sm" data-testid="visit-lead-fixed">
              <div className="font-semibold">{lead.customer_name}</div>
              <div className="text-muted-foreground text-xs">{lead.formatted_address || lead.address || ''}</div>
            </div>
          ) : (
            <div>
              <Label>{t.leads} *</Label>
              <Select value={form.lead_id} onValueChange={(v) => set('lead_id', v)} disabled={isEdit}>
                <SelectTrigger data-testid="visit-lead-select" className="h-11 mt-1"><SelectValue placeholder={t.selectLead} /></SelectTrigger>
                <SelectContent>
                  {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.customer_name}{l.city ? ` · ${l.city}` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t.visitDate}</Label>
              <Input data-testid="visit-date-input" type="date" value={form.date} onChange={(e) => set('date', e.target.value)} className="h-11 mt-1" required />
            </div>
            <div>
              <Label>{t.visitTime}</Label>
              <Input data-testid="visit-time-input" type="time" value={form.time} onChange={(e) => set('time', e.target.value)} className="h-11 mt-1" required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t.visitTypeLabel}</Label>
              <Select value={form.type} onValueChange={(v) => set('type', v)}>
                <SelectTrigger data-testid="visit-type-select" className="h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((x) => <SelectItem key={x} value={x} data-testid={`visit-type-opt-${x}`}>{t.visitType[x]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t.duration}</Label>
              <Input data-testid="visit-duration-input" type="number" min="15" step="15" value={form.duration_min} onChange={(e) => set('duration_min', e.target.value)} className="h-11 mt-1 font-mono" />
            </div>
          </div>
          {user?.role === 'manager' && team.length > 0 && (
            <div>
              <Label>{t.assignedTo}</Label>
              <Select value={form.assigned_to} onValueChange={(v) => set('assigned_to', v)}>
                <SelectTrigger data-testid="visit-assigned-select" className="h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {team.map((m) => <SelectItem key={m.username} value={m.username}>{m.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>{t.visitNotes}</Label>
            <Textarea data-testid="visit-notes-input" value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} className="mt-1" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" className="h-11" onClick={() => onOpenChange(false)} data-testid="visit-form-cancel">{t.cancel}</Button>
            <Button type="submit" className="h-11 font-semibold" disabled={saving} data-testid="visit-form-submit">{saving ? t.loading : t.save}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
